<template lang="pug">
  .knob-control(
    v-if="val"
    :style="{ width: size + 'px', height: size + 'px'}")

    svg(
      ref="knob"
      :width="size"
      :height="size"
      :viewBox="'0 0 ' + size + ' ' + size")

      defs
        filter#innershadow(width="160%" height="160%" x="-30%" y="-30%")
          feFlood(flood-color="#000000" flood-opacity=".8")
          feComposite(in2="SourceAlpha" operator="out")
          feGaussianBlur(stdDeviation="3" result="blur")
          feComposite(operator="atop" in2="SourceGraphic")

      path.knob-control__range(
        :d="rangePath"
        :stroke-width="strokeWidth"
        :stroke="rangeColor"
        :stroke-linecap="stokeCup"
        :style="{ 'stroke-opacity': opacity }"
        filter="url(#innershadow)"
        @click="onClick")

      //-path.knob-control__value(
        :d="valuePath"
        :stroke-width="strokeWidth"
        :stroke="valueColor")

      path.knob-control__button(
      v-if="!readonly"
        :d="buttonLeftPath"
        :stroke-width="strokeWidth"
        stroke="#444444"
        @click="onLeftButtonClick")

      path.knob-control__button(
        v-if="!readonly"
        :d="buttonRightPath"
        :stroke-width="strokeWidth"
        stroke="#444444"
        @click="onRightButtonClick")

      circle.knob-control__cursor(
        v-if="!readonly"
        :cx="cursor.x"
        :cy="cursor.y"
        :r="(strokeWidth * 0.9) / 2"
        fill="white"
        @mousedown="onMouseDown"
        @mouseup="onMouseUp"
        @mouseMove="onMouseMove"
        @touchstart="onMouseDown"
        @touchend="onMouseUp"
        @touchmove="onMouseMove")

      text.knob-control__text(
        :x=".57 * size"
        :y=".53 * size"
        :fill="textColor"
        text-anchor="end")
        | {{ integer }}
        //- tspan.knob-control__text__unit {{ units }}
        //- tspan.knob-control__text__decimal(v-if="stepSize < 1") {{ decimal }}

      text.knob-control__decimal(
        v-if="!readonly && showDecimal"
        :x=".58 * size"
        :y=".53 * size"
        :fill="textColor"
        text-anchor="start")
        | {{ decimal }}

      text.knob-control__unit(
        v-if="!readonly"
        :x=".57 * size"
        :y=".43 * size"
        :fill="textColor"
        text-anchor="start")
        | {{ units }}

      text.knob-control__text__mode(
        :x=".5 * size"
        :y=".63 * size"
        :fill="textColor"
        text-anchor="middle")
        | {{ mode }}

      text.knob-control__button--left(
        v-if="!readonly"
        :x=".35 * size"
        :y=".94 * size"
        fill="#FFFFFF"
        text-anchor="middle"
        @click="onLeftButtonClick")
        | -

      text.knob-control__button--right(
        v-if="!readonly"
        :x=".65 * size"
        :y=".92 * size"
        fill="#FFFFFF"
        text-anchor="middle"
        @click="onRightButtonClick")
        | +
</template>

<script>
import { MODES } from '@/app.config';

export default {
  name: 'Knob',
  data() {
    return {
      val: this.value,
      previousVal: 0,
      mousebutton: false,
      rangePath: '',
      valuePath: '',
      buttonLeftPath: '',
      buttonRightPath: '',
      cursor: {},
      opacity: 1,
    };
  },
  props: {
    size: {
      type: Number,
      default: 150,
    },
    angleOffset: {
      type: Number,
      default: 45,
    },
    angleArc: {
      type: Number,
      default: 270,
    },
    value: {
      type: Number,
      default: 60,
    },
    units: {
      type: String,
      default: 'F',
    },
    mode: {
      type: String,
      default: 'Auto',
    },
    modeValue: {
      type: Number,
      default: 0,
    },
    valMin: {
      type: Number,
      default: 1,
    },
    valMax: {
      type: Number,
      default: 100,
    },
    stepSize: {
      type: Number,
      default: 1,
    },
    showDecimal: {
      type: Boolean,
      default: false,
    },
    rangeColor: {
      type: String,
      default: '#E1E3E4',
    },
    valueColor: {
      type: String,
      default: '#0099cc',
    },
    textColor: {
      type: String,
      default: '#111111',
    },
    strokeWidth: {
      type: Number,
      default: 20,
    },
    stokeCup: {
      type: String,
      default: 'butt',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    blocked: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    value() {
      if (this.val !== this.value) {
        this.val = this.value;
        this.redraw();
      }
    },
    valMin() {
      this.redraw();
    },
    valMax() {
      this.redraw();
    },
    modeValue() {
      // this.redraw();
    },
    readonly() {
      this.redraw();
    },
  },
  computed: {
    getValue() {
      const step = 1 / this.stepSize;
      return Math.floor(this.val * step) / step;
    },
    integer() {
      return Math.floor(this.val);
    },
    decimal() {
      return 10 * (this.val % 1);
    },
  },
  methods: {
    /*
     * Commit value, indicating that it is no longer temporary.
     */
    commit() {
      this.previousVal = this.getValue;
      this.redraw();
      // this.$emit('input', this.val);
      this.$emit('changeKnob', this.getValue);
    },

    /*
     * Convert Polar coordinates to Cartesian.
     */
    polarToCartesian(centerX, centerY, radius, angleInDegrees) {
      const angleInRadians = ((angleInDegrees + 90) * Math.PI) / 180.0;

      return {
        x: centerX + radius * Math.cos(angleInRadians),
        y: centerY + radius * Math.sin(angleInRadians),
      };
    },

    /*
     * Return a path of an arc.
     */
    drawArc(x, y, radius, offset, angle) {
      const start = this.polarToCartesian(x, y, radius, angle + offset);
      const end = this.polarToCartesian(x, y, radius, offset);
      const largeArcFlag = angle <= 180 ? '0' : '1';

      // prettier-ignore
      const d = ['M', start.x, start.y, 'A', radius, radius, 0, largeArcFlag, 0, end.x, end.y].join(' ');

      return d;
    },

    /*
     * Redraw the knob on the canvas.
     */
    redraw() {
      if (!this.val || !this.valMin || !this.valMax) return;

      if (this.val < this.valMin) {
        this.val = this.valMin;
      } else if (this.val > this.valMax) {
        this.val = this.valMax;
      }

      const centerX = 0.5 * this.size;
      const centerY = 0.5 * this.size;
      const radius = 0.5 * (this.size - this.strokeWidth);
      const relValue = (this.val - this.valMin) / (this.valMax - this.valMin);
      const relAngle = relValue * this.angleArc;

      if (!this.readonly) {
        this.rangePath = this.drawArc(centerX, centerY, radius, 20, 320);
        this.valuePath = this.drawArc(centerX, centerY, radius, this.angleOffset, relAngle);
        this.cursor = this.polarToCartesian(centerX, centerY, radius, relAngle + this.angleOffset);
        this.buttonLeftPath = this.drawArc(centerX, centerY, radius, 0.2, 42.8);
        this.buttonRightPath = this.drawArc(centerX, centerY, radius, -43, 42.8);
        if (this.modeValue === MODES.COOLING) {
          this.opacity = 1 - (relAngle / this.angleArc) * (1 - 0.65);
        } else {
          this.opacity = (relAngle / this.angleArc) * (1 - 0.65) + 0.65;
        }
      } else {
        this.rangePath = this.drawArc(centerX, centerY, radius, 0, 359.9);
      }
    },

    /*
     * Sets the value of this knob.
     */
    setValue(value) {
      this.setValueFloating(value);
      this.commit();
    },

    /*
     * Sets floating (temporary) value of this knob.
     */
    setValueFloating(value) {
      /*
       * Clamp the actual value into the [valMin; valMax] range.
       */
      if (value < this.valMin) {
        value = this.valMin;
      } else if (value > this.valMax) {
        value = this.valMax;
      }

      const step = 1 / this.stepSize;
      value = Math.round(value * step) / step;
      this.val = value;
      this.redraw();
    },

    /*
     * Convert mouse event to value.
     */
    mouseEventToValue(e) {
      e = e.changedTouches ? e.changedTouches[0] : e;

      const bodyRect = document.body.getBoundingClientRect();
      const rect = this.$refs.knob.getBoundingClientRect();
      const rectX = rect.x || rect.left;
      const rectY = rect.y || rect.top;
      const offsetX = e.clientX - (rectX - bodyRect.left);
      const offsetY = e.clientY - (rectY - bodyRect.top);
      const dx = offsetX - this.size / 2;
      const dy = this.size / 2 - offsetY;
      const angleInRadians = Math.atan2(dx, dy);
      const angleInDegrees = angleInRadians * (180 / Math.PI);

      /*
       * I add 180 degrees so that my zero is down and the rest of the offset
       */
      let angle = angleInDegrees + 180 - this.angleOffset;

      /*
       * Make negative angles positive.
       */
      if (angle < 0) {
        angle += 360;
      }

      const { valMin, valMax } = this;
      let value = (angle / this.angleArc) * (valMax - valMin) + valMin;
      /*
       * Clamp values into valid interval.
       */
      if (value < valMin || value > valMax) {
        value = angleInDegrees > 0 ? valMax : valMin;
      }

      return value;
    },

    /*
     * This is called when the mouse button is depressed.
     */
    onMouseDown(e) {
      /*
       * If is blocked send value without changes
       */
      if (this.blocked) {
        this.$emit('changeKnob', this.getValue);
        return;
      }

      /*
       * If knob is not read-only, process mouse event.
       */
      if (!this.readonly) {
        e.preventDefault();
        const val = this.mouseEventToValue(e);
        this.setValueFloating(val);

        this.mousebutton = true;
        window.addEventListener('mousemove', this.onMouseMove);
        window.addEventListener('mouseup', this.onMouseUp);
      }
    },

    /*
     * This is called when the mouse cursor is moved.
     */
    onMouseMove(e) {
      const btn = this.mousebutton;

      /*
       * Only process event, if mouse button is depressed.
       */
      if (btn) {
        /*
         * If is blocked send value without changes
         */
        if (this.blocked) {
          this.$emit('changeKnob', this.getValue);
          return;
        }

        /*
         * If knob is not read-only, process mouse event.
         */
        if (!this.readonly) {
          e.preventDefault();
          const val = this.mouseEventToValue(e);
          this.setValueFloating(val);
        }
      }
    },

    /*
     * This is called when the mouse button is released.
     */
    onMouseUp(e) {
      const btn = this.mousebutton;

      /*
       * Only process event, if mouse button was depressed.
       */
      if (btn) {
        /*
         * If is blocked send value without changes
         */
        if (this.blocked) {
          this.$emit('changeKnob', this.getValue);
          return;
        }

        /*
         * If knob is not read-only, process mouse event.
         */
        if (!this.readonly) {
          e.preventDefault();
          const val = this.mouseEventToValue(e);
          this.setValue(val);

          window.removeEventListener('mousemove', this.onMouseMove);
          window.removeEventListener('mouseup', this.onMouseUp);
        }
      }

      this.mousebutton = false;
    },

    /*
     * This is called when the mouse button is released.
     */
    onClick(e) {
      /*
       * If is blocked send value without changes
       */
      if (this.blocked) {
        this.$emit('changeKnob', this.getValue);
        return;
      }

      /*
       * If knob is not read-only, process mouse event.
       */
      if (!this.readonly) {
        e.preventDefault();
        const val = this.mouseEventToValue(e);
        this.setValue(val);
      }
    },

    /*
     * This is called when click in the left button
     */
    onLeftButtonClick(e) {
      /*
       * If is blocked send value without changes
       */
      if (this.blocked) {
        this.$emit('changeKnob', this.getValue);
        return;
      }

      /*
       * If knob is not read-only, process mouse event.
       */
      if (!this.readonly) {
        e.preventDefault();
        const val = this.val - this.stepSize;
        this.setValue(val);
      }
    },

    onRightButtonClick(e) {
      /*
       * If is blocked send value without changes
       */
      if (this.blocked) {
        this.$emit('changeKnob', this.getValue);
        return;
      }

      /*
       * This is called when click in the right button.
       */
      if (!this.readonly) {
        e.preventDefault();
        const val = this.val + this.stepSize;
        this.setValue(val);
      }
    },
  },
  mounted() {
    this.redraw();
  },
};
</script>

<style>
.knob-control svg {
  filter: drop-shadow(0 0 5px rgba(0, 0, 0, 0.3));
}
.knob-control__range {
  fill: none;
  transition: stroke 0.1s ease-in;
}
.knob-control__value,
.knob-control__button {
  fill: none;
}

.knob-control__cursor {
  cursor: pointer;
  transform: all 0.3s;
}

.knob-control__text {
  font-size: 3.4rem;
  user-select: none;
}

.knob-control__text__unit {
  font-size: 0.4em;
  user-select: none;
}

.knob-control__decimal {
  font-size: 1.8rem;
  user-select: none;
}

.knob-control__text__mode {
  font-size: 1.1rem;
}

.knob-control__button {
  cursor: pointer;
}

.knob-control__button--left {
  font-size: 3rem;
  cursor: pointer;
  user-select: none;
}
.knob-control__button--right {
  font-size: 2rem;
  cursor: pointer;
  user-select: none;
}
</style>
